import pickExcept from 'utils/pickExcept';
import pick from 'utils/pick';
import uniq from 'utils/uniq';
import uniqBy from 'utils/uniqBy';
import uuid from 'utils/uuid';
import { isUrlValid } from 'utils/validator';
import queryString from 'utils/queryString';
import ISSUE_KIND from './issueKind.js';
import {
  getAssetUrl,
  getCurrentPhoto,
  getOriginalPage,
  getTemplatePage,
  isBackgroundPlaceholder,
  isFrameInSafeZone,
  isPhotoPlaceholder,
  isStaticPlaceholder,
  isTextPlaceholder,
  isVectorPlaceholder,
  normalizeTextPlaceholderValue,
  PLACEHOLDER_TYPE,
} from './template';
import getPlaceholderSpec from './polotno/placeholder/getPlaceholderSpec.js';
import { getEffectiveDpi, getPhotoDpiThresholds } from './photoDpi';

const DONT_PICK_THESE_PAGE_KEYS = [
  'id',
  'index',
  'type',
  'spine',
  'span',
  'pageNumber',
  'pageLabel',
  'pageType',
  'placeholders',
  'override',
];

// Materialize a draft custom text style into the placeholder override for the
// one-way backend export. The render backend (Prawn/pdfer) never receives
// `customStyles` and `Style.find` returns {} for a non-template style, so a custom
// style's look must live in the override. Only custom styles are flattened —
// template styles the backend resolves itself. This is the authoritative point
// because formatData sees every page, not just the one open in the editor.
//
// `styleName` is resolved by the caller: `placeholder.override?.styleName ??
// placeholder.styleName ?? <template placeholder styleName>`. The template fallback
// matters — a page never opened in the editor links to its style only through the
// TEMPLATE placeholder, so without it the preview/sync payload would render the
// template look while the PDF (validator path, which merges the template) renders the
// custom one. See docs/custom-text-styles.md.
const materializeTextOverride = (customStyles, styleName, override) => {
  if (!styleName || customStyles == null) {
    return override;
  }
  const [category, name] = styleName.split('.');
  const customStyle = customStyles[category]?.[name];
  if (customStyle == null) {
    return override;
  }
  // The element's own override (manual divergence) wins over the style.
  return { ...getPlaceholderSpec(customStyle), ...(override ?? {}) };
};

// Key → photo index per `photos` array, shared across formatData calls. Page previews
// convert one page at a time with the photos of the whole book, so a per-call index
// would be rebuilt for every page. Editor state is immutable (immer), so a changed
// photo list arrives as a new array. The length check guards against in-place
// appends (parseData pushes into its input `photos`).
const photosIndexCache = new WeakMap();

const getPhotosByKey = photos => {
  if (!Array.isArray(photos)) {
    return undefined;
  }
  const cached = photosIndexCache.get(photos);
  if (cached && cached.length === photos.length) {
    return cached.photosByKey;
  }
  // First occurrence wins to match Array#find semantics.
  const photosByKey = new Map();
  photos.forEach(photo => {
    if (photo && !photosByKey.has(photo.key)) {
      photosByKey.set(photo.key, photo);
    }
  });
  photosIndexCache.set(photos, { length: photos.length, photosByKey });
  return photosByKey;
};

const formatOverride = override => {
  if (override == null) {
    return undefined;
  }
  if (typeof override !== 'object') {
    return undefined;
  }
  if (Object.keys(override).length === 0) {
    return undefined;
  }
  return override;
};

/**
 * Convert server data (order, draft, or cart) to client editor state.
 *
 * The server stores photos as URLs and metadata scattered across placeholders.
 * This function collects photos into a deduplicated array and maps each placeholder
 * to a normalized client structure with a `photo_key` reference — so the editor
 * can manage photos independently from pages.
 *
 * Each placeholder type maps differently:
 * - Photo: resolves the URL (from value, defaultValue, or value_url), deduplicates
 *   into the photos array, and stores position/scale/orientation in `data`.
 * - Text: normalizes the value via `normalizeTextPlaceholderValue`, preserves
 *   original and enhanced variants.
 * - Background: extracts the URL into a page-level `backgroundImage` override.
 * - Vector / Static: passes through id, type, override, and polotno data.
 *
 * @param {object} template - Product template definition. Provides asset URL resolution
 *   and custom font extraction.
 * @param {object} data - Server-side data payload.
 * @param {object} [data.cover] - Cover page data, if the template has a cover.
 * @param {Array} [data.pages] - Non-cover page array.
 * @param {Array} [data.photos] - Pre-existing photo array from the server.
 * @param {object} [data.additional_info] - Extra metadata, passed through as `additionalInfo`.
 * @param {object} [data.favorite] - User favorites, passed through.
 * @param {object} config - Optional configuration.
 * @param {boolean} [config.onlyFirstPage] - Parse only the first page. Useful for thumbnails.
 * @param {boolean} [config.useServerAssets] - Resolve default values from `assets_server`
 *   instead of `assets_client`.
 * @param {boolean} [config.pdfer] - Enable PDF renderer mode. Preserves photo orientation
 *   during deduplication.
 * @param {string[]} [config.withPageParams] - Extra page keys to include in the output.
 * @returns {{ photos: Array, pages: Array, favorite: object, additionalInfo: object }}
 */
export function parseData(template, data = {}, config = {}) {
  const photos = data.photos || [];
  const pages = [];
  const additionalInfo = data.additional_info || {};
  const favorite = data.favorite || {};

  let originalPages = [];
  if (data.cover) {
    originalPages.push({ ...data.cover, type: 'cover' });
  }
  if (data.pages) {
    originalPages.push(...data.pages.map(page => ({ ...page, type: 'pages' })));
  }

  if (config.onlyFirstPage) {
    originalPages = originalPages.slice(0, 1);
  }

  let duplicatePlaceholders = 0;

  originalPages.forEach(page => {
    const pageOverride = { ...(page.override ?? {}) };
    const placeholders = [];

    // specs defines dynamic placeholders
    const specs = page.specs ?? [];

    (page.placeholders || []).forEach(placeholder => {
      const placeholderSpec = specs.find(spec => spec.id === placeholder.id) ?? {};
      const override = { ...placeholderSpec, ...placeholder.override };
      const polotno = placeholder.polotno ?? undefined;

      if (isBackgroundPlaceholder(placeholder)) {
        const url = placeholder.value;
        if (isUrlValid(url)) {
          pageOverride.backgroundImage = url;
        }
        return;
      }

      if (isPhotoPlaceholder(placeholder)) {
        let url = placeholder.value;
        let mediumId = placeholder.mediumId || undefined;

        const defaultValue =
          placeholder.defaultValue || placeholder.photo_defaultValue || undefined;
        const urlFromServer = placeholder.value_url;
        if (defaultValue) {
          url = getAssetUrl(
            template,
            `/${config.useServerAssets ? 'assets_server' : 'assets_client'}/images/${defaultValue}`,
          );
        } else if (urlFromServer) {
          if (urlFromServer.indexOf('X-Amz-Signature=') >= 0) {
            url = queryString.parseUrl(urlFromServer).url;
          } else {
            url = urlFromServer;
          }
          if (typeof placeholder.value === 'number') {
            mediumId = placeholder.value;
          }
        }

        let photoData = {};
        if (url) {
          let index = photos.findIndex(photo => photo.url === url && (!config.pdfer || photo.orientation === placeholder.orientation));
          if (index < 0) {
            const getTags = () => {
              const tags = [];
              // original tags
              if (Array.isArray(placeholder.photo_tags)) {
                tags.push(...placeholder.photo_tags);
              }
              // in some cases, tags are not saved
              if (placeholder.id.indexOf('layer_') === 0) {
                tags.push('layer');
              }
              return uniq(tags);
            };

            index =
              photos.push({
                key: uuid(),
                url,
                defaultValue,
                alive: 1,
                mediumId,
                width: placeholder.photo_width || undefined,
                height: placeholder.photo_height || undefined,
                datetime: placeholder.photo_datetime || undefined,
                tags: getTags(),
                type: placeholder.photo_type || undefined,
                ...(config.pdfer ? { orientation: placeholder.orientation ?? 0 } : {})
              }) - 1;
          }

          photoData = {
            photo_key: photos[index].key,
            x: placeholder.center[0],
            y: placeholder.center[1],
            scale: placeholder.scale,
            orientation: placeholder.orientation,
          };
        }

        placeholders.push({
          id: placeholder.id,
          type: placeholder.type,
          override,
          polotno,
          data: photoData,
        });
        return;
      }

      if (isTextPlaceholder(placeholder)) {
        let textData = {};
        if (placeholder.value) {
          textData = {
            value: normalizeTextPlaceholderValue(placeholder.value),
            value_original: placeholder.value_original,
            value_enhanced: placeholder.value_enhanced,
          };
        }

        placeholders.push({
          id: placeholder.id,
          type: PLACEHOLDER_TYPE.TEXT,
          override,
          polotno,
          data: textData,
        });
        return;
      }

      if (isVectorPlaceholder(placeholder)) {
        placeholders.push({ id: placeholder.id, type: PLACEHOLDER_TYPE.VECTOR, override, polotno });
        return;
      }

      if (isStaticPlaceholder(placeholder)) {
        placeholders.push({ id: placeholder.id, type: PLACEHOLDER_TYPE.STATIC, override, polotno });
        return;
      }

      console.error(`Unsupported placeholder ${placeholder.id} type "${placeholder.type}"!`);
    });

    const uniqPlaceholders = uniqBy(placeholders, 'id');

    pages.push({
      key: uuid(),
      id: page.id,
      ...pickExcept(page, ...DONT_PICK_THESE_PAGE_KEYS),
      ...pick(page, ...(config.withPageParams ?? [])),
      type: page.type,
      placeholders: uniqPlaceholders,
      override: pageOverride,
    });

    if (uniqPlaceholders.length !== placeholders.length) {
      duplicatePlaceholders += placeholders.length - uniqPlaceholders.length;
    }
  });

  if (duplicatePlaceholders > 0) {
    console.log({ duplicatePlaceholders });
  }

  if (data.photos) {
    console.log(`Photos from pages: ${photos.length}. Photos from data: ${data.photos.length}`);
  }

  return {
    photos,
    pages,
    favorite,
    additionalInfo,
  };
}

/**
 * Convert client editor state to server format for draft saves and cart submissions.
 *
 * Each placeholder serializes differently by type:
 * - Photo: resolves the photo by `photo_key`, writes back position, scale, orientation, and metadata.
 * - Text: normalizes the value, preserves original and enhanced variants.
 * - Background: extracts the photo URL into a photo-type placeholder.
 * - Vector / Static: passes through id, type, override, and polotno data.
 *
 * When `config.validator` is present (cart/order submission), the function enforces
 * completeness rules — missing photos or text trigger critical errors, photos below the
 * critical DPI floor block, photos below the product's warning DPI (`photoDpi.js`) and
 * text outside the safe zone trigger warnings.
 * Without a validator (draft save), empty placeholders are preserved so partial work persists.
 *
 * @param {object} template - Product template definition. Provides original page structure,
 *   cover detection, safe zones, and bleed settings.
 * @param {object} data - Client editor state.
 * @param {Array} data.pages - Client-side pages with placeholders and their `data` objects.
 * @param {Array} data.photos - Photo array. Each placeholder references a photo by `photo_key`.
 * @param {object} data.additionalInfo - Extra metadata, passed through as `additional_info`.
 * @param {object} data.favorite - User favorites, passed through.
 * @param {object} config - Optional configuration.
 * @param {object} [config.validator] - Enables validation when set. Exposes `.critical()` and
 *   `.warning()` methods, plus `.config.requiredPhotos`, `.config.requiredTexts`, and
 *   `.config.withQualityCheck` flags.
 * @param {string[]} [config.withPageParams] - Extra page keys to include in the output.
 * @returns {{ additional_info: object, favorite: object, cover?: object, pages: Array, photos: Array }}
 */
export function formatData(template, { pages, photos, additionalInfo, favorite, customStyles }, config = {}) {
  const data = {
    additional_info: additionalInfo ?? undefined,
    favorite: favorite ?? {},
    pages: [],
    photos: photos ?? undefined,
  };

  // Resolved lazily: pages without photo placeholders never touch the index.
  let photosByKey;
  const getIndex = () => {
    if (photosByKey === undefined) {
      photosByKey = getPhotosByKey(photos);
    }
    return photosByKey;
  };

  const isCoverPage = (page, index) => {
    if (page.type === 'cover') {
      return true;
    }
    if (page.type === 'pages') {
      return false;
    }
    return template.cover && index === 0;
  };

  pages.forEach((page, index) => {
    if (page == null) {
      return;
    }
    const pageOverride = { ...(page.override ?? {}) };
    const isCover = isCoverPage(page, index);
    // When validator is not null, it means prepare data for the server.
    // So it is useless to format placeholders which are not the part of the original page.
    // Otherwise, without validator, save all of data as a draft.
    const originalPage = config.validator
      ? getOriginalPage(template, page, isCover ? 'cover' : 'pages')
      : page;

    // Style link fallback: a page never opened in the editor carries its style link
    // only on the TEMPLATE placeholder, not the draft one. The validator branch above
    // already merges the template (getOriginalPage) so its placeholders carry the link;
    // the draft/preview branch does not, so look it up here to keep custom-style
    // materialization consistent across both. Guard on the section existing, matching
    // getOriginalPage — getTemplatePage would throw on a template without it.
    // See docs/custom-text-styles.md.
    const pageType = isCover ? 'cover' : 'pages';
    const templateStyleNameById =
      config.validator || !template[pageType]
        ? null
        : new Map(
            (getTemplatePage(template, page.id, pageType)?.placeholders ?? []).map(p => [
              p.id,
              p.styleName,
            ]),
          );

    // Structured issue text: `{pagePrefix}{description}`. Single-page
    // products drop the prefix because there's no other page to contrast
    // against; multi-page products lead with "Page N: " (or "Cover Page: ")
    // so the page lives in a stable, scannable position at the start of
    // each row in both the modal and the sidebar.
    let pagePrefix = '';
    if (pages.length > 1) {
      pagePrefix = isCover
        ? 'Cover Page: '
        : `Page ${template.cover ? index : index + 1}: `;
    }

    const locatorFor = (placeholder, kind) => ({
      pageIndex: index,
      // Item page key — what `withPolotno`'s `navigateToPage(pageKey)`
      // expects when the click crosses pages in a per-page editor.
      pageKey: page.key,
      // Template page key — matches `polotno store page.custom.originalPage.key`
      // for finding the page directly in the live store (works for both
      // posters and the currently-open page in a per-page editor).
      originalPageKey: originalPage?.key,
      isCover,
      elementId: placeholder.id,
      kind,
    });

    const placeholders = [];

    (originalPage.placeholders || []).forEach(placeholder => {
      const override = formatOverride(placeholder.override);
      const polotno = placeholder.polotno ?? undefined;

      if (isBackgroundPlaceholder(placeholder)) {
        const url = placeholder.data.photo_url;
        if (isUrlValid(url)) {
          placeholders.push({
            id: placeholder.id,
            type: PLACEHOLDER_TYPE.PHOTO,
            center: [0.5, 0.5],
            value: url,
            orientation: 0,
            scale: 1,
            override,
            polotno,
          });

          pageOverride.backgroundImage = undefined;
        }
        return;
      }

      if (isPhotoPlaceholder(placeholder)) {
        let isPhotoValid = false;

        const photoData = placeholder.data || {};

        const { photo, defaultValue, error } = getCurrentPhoto(
          photos,
          { key: photoData.photo_key },
          getIndex(),
        );

        if (defaultValue) {
          placeholders.push({
            id: placeholder.id,
            type: placeholder.type,
            center: [0.5, 0.5],
            defaultValue,
            orientation: 0,
            scale: 1,
            override,
            polotno,

            photo_width: photo.width || undefined,
            photo_height: photo.height || undefined,
            photo_datetime: photo.datetime || undefined,
            photo_tags: photo.tags || [],
            photo_type: photo.type || undefined,
          });

          isPhotoValid = true;
        } else if (photo) {
          placeholders.push({
            id: placeholder.id,
            type: placeholder.type,
            center: [photoData.x ?? 0.5, photoData.y ?? 0.5],
            value: photo.url,
            mediumId: photo.mediumId || undefined,
            orientation: photoData.orientation ?? 0,
            scale: photoData.scale ?? 1,
            override,
            polotno,

            photo_width: photo.width || undefined,
            photo_height: photo.height || undefined,
            photo_datetime: photo.datetime || undefined,
            photo_tags: photo.tags || [],
            photo_type: photo.type || undefined,
          });

          isPhotoValid = true;
        } else {
          if (!config.validator) {
            // Add also empty placeholder when validator is undefined (draft)
            placeholders.push({
              id: placeholder.id,
              type: placeholder.type,
              override,
              polotno,
            });
          }

          isPhotoValid = false;
        }

        if (config.validator) {
          if (!isPhotoValid) {
            if (placeholder.required || config.validator.config.requiredPhotos) {
              config.validator.critical(
                `${pagePrefix}Missing photo`,
                locatorFor(placeholder, ISSUE_KIND.MISSING_PHOTO),
              );
            } else if (error) {
              config.validator.critical(
                `${pagePrefix}Broken photo — ${error}`,
                locatorFor(placeholder, ISSUE_KIND.BROKEN_PHOTO),
              );
            }
          } else {
            // Skip DPI check for template-supplied assets (defaults, layers,
            // embellishments, backgrounds). Their source size is intentionally
            // small and they are not user-replaceable, so flagging them as
            // "low quality" on pages where the user sees no photo is confusing.
            const isTemplateAsset =
              !!defaultValue ||
              (Array.isArray(photo.tags) &&
                photo.tags.some(t => t === 'layer' || t === 'embellish' || t === 'background'));

            if (!isTemplateAsset && config.validator.config.withQualityCheck !== false) {
              const frameWidth = override?.frame?.[2] ?? placeholder.frame[2];
              const frameHeight = override?.frame?.[3] ?? placeholder.frame[3];

              if (photo.width && photo.height && frameWidth && frameHeight) {
                const currentDPI = getEffectiveDpi(
                  photo,
                  { width: frameWidth, height: frameHeight },
                  { scale: photoData.scale, orientation: photoData.orientation },
                );
                const thresholds = getPhotoDpiThresholds(template);

                // Low DPI never blocks the cart: the user may accept the print.
                // Below `critical` the wording is stronger than below the
                // (template-tunable) `min`.
                if (currentDPI < thresholds.critical) {
                  config.validator.warning(
                    `${pagePrefix}Photo is too small to print at this size`,
                    locatorFor(placeholder, ISSUE_KIND.LOW_DPI),
                  );
                } else if (currentDPI < thresholds.min) {
                  config.validator.warning(
                    `${pagePrefix}Photo resolution is low and may print soft`,
                    locatorFor(placeholder, ISSUE_KIND.LOW_DPI),
                  );
                }
              }
            }
          }
        }
        return;
      }

      if (isTextPlaceholder(placeholder)) {
        let isTextValid = false;

        const textData = placeholder.data || {};

        const value = textData.value ?? '';

        // Flatten a draft custom style into the override so the backend renders it.
        const styleName =
          placeholder.override?.styleName ??
          placeholder.styleName ??
          templateStyleNameById?.get(placeholder.id);
        const textOverride = materializeTextOverride(customStyles, styleName, override);

        if (value.length > 0) {
          placeholders.push({
            id: placeholder.id,
            type: PLACEHOLDER_TYPE.TEXT,
            value: normalizeTextPlaceholderValue(value),
            value_original: textData.value_original,
            value_enhanced: textData.value_enhanced,
            override: textOverride,
            polotno,
          });

          isTextValid = true;
        } else if (!config.validator) {
          // Add also empty placeholder when validator is undefined (draft)
          placeholders.push({
            id: placeholder.id,
            type: PLACEHOLDER_TYPE.TEXT,
            override: textOverride,
            polotno,
          });

          isTextValid = false;
        }

        if (config.validator) {
          if (!isTextValid && config.validator.config.requiredTexts) {
            config.validator.critical(
              `${pagePrefix}Missing text`,
              locatorFor(placeholder, ISSUE_KIND.MISSING_TEXT),
            );
          }

          // KNOWN ISSUE: false-positive "Element placed near page edge" for
          // generator-produced text placeholders (notably fh-calendar).
          // The generator emits `override.frame` describing the placeholder
          // BOX dimensions; for top-vAligned text Polotno is configured with
          // `height: undefined` (auto-size) in convertTextToPolotno, so once
          // the user opens the page Polotno saves back a frame shrunken to
          // the actual text height. The check below uses the original (tall)
          // box-frame and flags it as bleeding into the safe zone, even
          // though the rendered text visually sits well inside. Reopening
          // the editor "fixes" the warning purely as a side effect of the
          // Polotno round-trip. See PR #157 / final-check sidebar discussion.
          if (originalPage.bleed && !isFrameInSafeZone(originalPage.safeFrame, override?.frame, override?.angle, originalPage.size.offsetX)) {
            config.validator.warning(
              `${pagePrefix}Element placed near page edge`,
              locatorFor(placeholder, ISSUE_KIND.SAFE_ZONE),
            );
          }
        }
        return;
      }

      if (isVectorPlaceholder(placeholder)) {
        placeholders.push({
          id: placeholder.id,
          type: PLACEHOLDER_TYPE.VECTOR,
          override,
          polotno,
        });
        // TODO validation?
        return;
      }

      if (isStaticPlaceholder(placeholder)) {
        placeholders.push({
          id: placeholder.id,
          type: PLACEHOLDER_TYPE.STATIC,
          override,
          polotno,
        });
        return;
      }

      console.error(`Unsupported placeholder ${placeholder.id} type "${placeholder.type}"!`);
    });

    const pageItem = {
      id: originalPage.id,
      ...pickExcept(page, ...DONT_PICK_THESE_PAGE_KEYS),
      ...pick(page, ...(config.withPageParams ?? [])),
      placeholders,
      override: formatOverride(pageOverride),
    };

    if (isCoverPage(page, index)) {
      data.cover = pageItem;
    } else {
      data.pages.push(pageItem);
    }
  });

  return data;
}
